import { useState, useEffect } from 'react';
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../ui/card';
import { useAuth } from "contexts/AuthContext";
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import { FullScreenLoading } from "components/ui/loader";
import { usePageHeader } from "contexts/PageHeaderContext";
import {
  Zap,
  Clock,
  Building2,
  CalendarClock,
  ArrowRight,
  Hotel,
  Star,
  Shield,
} from 'lucide-react';
import { getMiqaatByOperator } from "services/tourService"; 


interface BookingFlow {
  id: string;
  title: string;
  subtitle: string;
  icon: React.ElementType;
  color: string;
  bgColor: string;
  borderColor: string;
  badgeText: string;
  badgeColor: string;
  buttonColor: string;
  requiresApproval: boolean;
  isInstant: boolean;
  route: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  params?: any;
}

interface MiqaatRotation {
  id: number;
  rotationId: string;
  rotationCode: string;
  rotationName: string;
  arrivalDate: string;
  departureDate: string;
  quota: number;
  remaining: number;
  isActive: boolean;
}

export function TourCreationDashboardPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { setPageHeader } = usePageHeader();
  
  const [miqaatRotations, setMiqaatRotations] = useState<MiqaatRotation[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setPageHeader({
      title: "Create New Tour",
      subtitle: "Select booking type to proceed",
      icon: <Hotel className="h-6 w-6" />,
      onBack: () => navigate("/tours"),
    });

    return () => setPageHeader(null);
  }, [navigate, setPageHeader]);

  const bookingFlows: BookingFlow[] = [
    {
      id: 'instant',
      title: 'Instant Booking',
      subtitle: 'Real-Time Availability',
      icon: Zap,
      color: 'text-green-600',
      bgColor: 'bg-green-50',
      borderColor: 'border-green-200',
      badgeText: 'Instant Confirmation',
      badgeColor: '!bg-green-50 !text-green-600 !border-green-400',
      buttonColor: '!bg-green-50 !text-green-600 !border-green-400 hover:!bg-green-100',
      requiresApproval: true,
      isInstant: true,
      route: '/tours/tour-create/instant'
    },
    {
      id: 'request',
      title: 'Request Faiz Tour',
      subtitle: 'Approval Required',
      icon: Clock,
      color: 'text-amber-600',
      bgColor: 'bg-amber-50',
      borderColor: 'border-amber-200',
      badgeText: 'Approval Needed',
      badgeColor: '!bg-amber-50 !text-amber-600 !border-amber-400',
      buttonColor: '!bg-amber-50 !text-amber-600 !border-amber-400 hover:!bg-amber-100',
      requiresApproval: true,
      isInstant: false,
      route: '/tours/tour-create',
      params: {
        selectedMiqaat: {
          type: "general"
        }
      }
    },
    {
      id: 'external',
      title: 'Request Hotel Tour',
      subtitle: 'Non-Managed Accommodation',
      icon: Building2,
      color: 'text-blue-600',
      bgColor: 'bg-blue-50',
      borderColor: 'border-blue-200',
      badgeText: 'External Inventory',
      badgeColor: '!bg-blue-50 !text-blue-600 !border-blue-400',
      buttonColor: '!bg-blue-50 !text-blue-600 !border-blue-400 hover:!bg-blue-100',
      requiresApproval: false,
      isInstant: true,
      route: '/tours/tour-create',
      params: {
        selectedMiqaat: {
          type: "general"
        }
      }
    }
  ];

  useEffect(() => {
  const fetchMiqaat = async () => {
    try {
      if (!user?.tourOperatorId) return; 

      setLoading(true);

      const tourOperatorId = Number(user.tourOperatorId); 
      

      const data = await getMiqaatByOperator(tourOperatorId);

      const mappedData: MiqaatRotation[] = data.map((item) => ({
        id: item.id,  
        rotationId: item.rotationId, 
        rotationCode: item.miqaatCode,
        rotationName: `${item.miqaatName} - ${item.rotationName}`,
        arrivalDate: item.arrivalDate,
        departureDate: item.departureDate,
        quota: item.totalQuota,
        remaining: item.availableQuota,
        isActive: true,
      }));

      setMiqaatRotations(mappedData);
    } catch (error) {
      console.error("Failed to fetch miqaat", error);
    } finally {
      setLoading(false);
    }
  };

  fetchMiqaat();
}, [user]);

const formatDate = (dateStr: string) => {
  return new Date(dateStr.split("T")[0]).toLocaleDateString("en-GB");
};

  const handleFlowSelection = async (flow: BookingFlow) => {
    try {
      setLoading(true);

      if (flow.id === 'request') {
        navigate("/tours/tour-create", {
          state: {
            selectedMiqaat: {
              type: "general",
            },
            flowType: flow.id,
            tourCategory: 3, // Faiz
          },
        });
        return;
      }

      if (flow.id === 'external') {
        navigate("/tours/tour-create", {
          state: {
            selectedMiqaat: {
              type: "general",
            },
            flowType: flow.id,
            tourCategory: 4, // External
          },
        });
        return;
      }

      if (flow.id === 'instant') {
        navigate(flow.route, {
          state: {
            tourCategory: 1, // Instant
          },
        });
        return;
      }
    } finally {
      setLoading(false);
    }
  };

  const handleMiqaatSelection = async (rotation: MiqaatRotation) => {
    try {
      setLoading(true);

      navigate("/tours/tour-create", {
        state: {
          tourCategory: 2,
          selectedMiqaat: {
            type: "package",
            packageId: rotation.id.toString(),
            packageName: rotation.rotationName,
            rotationId: rotation.id,
            arrivalDate: rotation.arrivalDate,
            departureDate: rotation.departureDate,
          },
        },
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {loading && <FullScreenLoading />}
      <div className="space-y-6 pb-8">
        {/* Miqaat Rotations Section - Show at top if available */}
        {miqaatRotations.length > 0 && (
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <CalendarClock className="w-5 h-5 text-purple-600" />
              <h2 className="text-xl font-semibold text-gray-900">Active Miqaat Rotations</h2>
              <Badge className="bg-purple-100 text-purple-700 border-purple-300">
                Priority Booking
              </Badge>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4" style={{ marginTop: "0px" }}>
              {miqaatRotations.map((rotation) => (
                <Card
                  key={rotation.id}
                  className="border-2 border-purple-200 hover:border-purple-400 hover:shadow-lg transition-all cursor-pointer bg-gradient-to-br from-purple-50 to-white"
                  onClick={() => handleMiqaatSelection(rotation)} 
                >
                  <CardContent className="p-6">
                    <div className="flex items-start justify-between" style={{ alignItems: "center" }}>
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center">
                          <Star className="w-6 h-6 text-purple-600" />
                        </div>
                        <div>
                          <h3 className="font-bold text-lg text-gray-900">{rotation.rotationName}</h3>
                          <p className="text-sm text-gray-600 font-mono">{rotation.rotationCode}</p>
                        </div>
                      </div>
                    </div>
                    <Badge className="bg-purple-100 text-purple-700 border-purple-300 mt-1 mb-1">
                      Rotation
                    </Badge>

                    <div className="space-y-3">
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-gray-600">Travel Dates:</span>
                        <span className="font-medium text-gray-900">
                          {formatDate(rotation.arrivalDate)} - {formatDate(rotation.departureDate)}
                        </span>
                      </div>
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-gray-600">Quota Status:</span>
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-gray-900">{rotation.remaining} / {rotation.quota}</span>
                          <Badge
                            className={
                              rotation.remaining > 50
                                ? 'bg-green-100 text-green-700 border-green-300'
                                : rotation.remaining > 20
                                ? 'bg-amber-100 text-amber-700 border-amber-300'
                                : 'bg-red-100 text-red-700 border-red-300'
                            }
                          >
                            {rotation.remaining > 50 ? 'Available' : rotation.remaining > 20 ? 'Limited' : 'Low'}
                          </Badge>
                        </div>
                      </div>
                    </div>

                    <Button
                      className="w-full mt-4 bg-purple-600 hover:bg-purple-700 text-white"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleMiqaatSelection(rotation);
                      }}
                    >
                      Select Rotation
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}

        {/* Standard Booking Flows Section */}
        <div className="space-y-4" style={{ marginTop: "10px" }}>
          <div className="flex items-center gap-2">
            <Shield className="w-5 h-5 text-gray-600" />
            <h2 className="text-xl font-semibold text-gray-900">Standard Booking Options</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {bookingFlows.map((flow) => {
              const IconComponent = flow.icon;
              return (
                <Card
                  key={flow.id}
                  className={`border-2 ${flow.borderColor} hover:shadow-xl transition-all cursor-pointer bg-white group`}
                  style={{ gap: "2px" }}
                  onClick={() => handleFlowSelection(flow)}
                >
                  <CardHeader className="pb-4">
                    <div className="flex items-start justify-between mb-2">
                      <div className={`w-10 h-10 rounded-xl ${flow.bgColor} flex items-center justify-center group-hover:scale-110 transition-transform`}>
                        <IconComponent className={`w-7 h-7 ${flow.color}`} />
                      </div>
                      <Badge className={flow.badgeColor}>
                        {flow.badgeText}
                      </Badge>
                    </div>
                    <CardTitle className="text-xl text-gray-900">{flow.title}</CardTitle>
                    <CardDescription className="text-sm font-medium text-gray-600">
                      {flow.subtitle}
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <Button
                      className={`w-full !border-2 ${flow.buttonColor} shadow-none transition-all`}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleFlowSelection(flow);
                      }}
                    >
                      {flow.isInstant ? 'Start Booking' : 'Submit Request'}
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </div>
    </>
  );
}
